/* eslint-disable jsx-a11y/anchor-is-valid */
import {
  AlertBox, ButtonColor, Form, FormActions, Link, Spacing, SpacingName, TypographyName, useToggle,
} from '@doveit/lego';
import React from 'react';
import PrivacyPolicyFooter from '../privacy-policy-footer/PrivacyPolicyFooter';
import { provinceSelectOptions } from './fieldPresets';
import validationSchema from './JobApplicationForm.schema';

export interface JobApplicationFormModel {
  name: string,
  phoneNumber: string,
  email: string,
  province: string,
  message: string,
  marketing: boolean,
}

export interface JobApplicationFormProps {
  ctaLabel?: string,
  onSubmit: (values: Partial<JobApplicationFormModel>, actions: FormActions<JobApplicationFormModel>) => void,
  showProvince?: boolean,
  dataRef?: string,
  dataTracking?: string,
  initialValues?: Partial<JobApplicationFormModel>,
  errorMessage?: string,
  successMessage?: string,
  loading?: boolean,
  verticalOnly?: boolean,
  submitColor?: ButtonColor,
}

const DEFAULT_INITIAL_VALUES: JobApplicationFormModel = {
  name: '',
  phoneNumber: '',
  email: '',
  province: '',
  message: '',
  marketing: false,
};

const JobApplicationForm: React.FC<JobApplicationFormProps> = ({
  ctaLabel,
  onSubmit,
  showProvince,
  dataRef = 'job-application-form',
  dataTracking,
  initialValues,
  errorMessage,
  successMessage,
  loading,
  verticalOnly,
  submitColor = ButtonColor.SECONDARY,
}) => {
  const { opened: isMessageFieldOpen, toggle } = useToggle();

  return (
    <Form
      dataRef={dataRef}
      onSubmit={onSubmit}
      initialValues={{
        ...DEFAULT_INITIAL_VALUES,
        ...initialValues,
      }}
      validationSchema={validationSchema}
      loading={loading}
    >
      {({ setFieldValue }) => {
        const onToggleButtonClick = () => {
          if (isMessageFieldOpen) {
            setFieldValue('message', '');
          }
          toggle();
        };

        return (
          <>
            <Form.Item>
              <Form.Input
                name="name"
                label="Nome"
                aria-label="Campo per inserire il tuo nome e cognome"
                placeholder="Nome e cognome"
                dataRef={`${dataRef}-name`}
              />
            </Form.Item>

            <Form.Item size={verticalOnly ? 1 : { MD: 1 / 2 }}>
              <Form.Input
                name="phoneNumber"
                required
                aria-label="Campo per inserire il tuo recapito telefonico"
                label="Telefono"
                placeholder="Numero di telefono"
                dataRef={`${dataRef}-phoneNumber`}
              />
            </Form.Item>

            <Form.Item size={verticalOnly ? 1 : { MD: 1 / 2 }}>
              <Form.Input
                name="email"
                aria-label="Campo per inserire l'email dell'agente"
                label="Email"
                placeholder="Indirizzo email"
                dataRef={`${dataRef}-email`}
              />
            </Form.Item>

            {showProvince && (
              <Form.Item>
                <Form.Select
                  name="province"
                  aria-label="Campo per inserire la tua zona di riferimento"
                  label="La tua zona di riferimento"
                  dataRef={`${dataRef}-province`}
                  options={provinceSelectOptions}
                  required
                />
              </Form.Item>
            )}

            <Form.Item>
              {isMessageFieldOpen && (
                <Form.TextArea
                  name="message"
                  aria-label="Campo per inserire un messaggio opzionale"
                  label="Messaggio"
                  placeholder="Raccontaci qualcosa su di te"
                  dataRef={`${dataRef}-message`}
                />
              )}
              <Link
                dataRef={isMessageFieldOpen ? 'remove-message' : 'add-message'}
                onClick={onToggleButtonClick}
                color="brand.primary"
              >
                {isMessageFieldOpen ? '- Rimuovi messaggio' : '+ Aggiungi messaggio'}
              </Link>
            </Form.Item>

            <Form.Item>
              <Form.Checkbox
                name="marketing"
                aria-label="Checkbox per selezionare il consenso al marketing"
                text="Accetto di ricevere comunicazioni e offerte da parte di Dove.it"
                textSize={TypographyName.SMALLER}
                dataRef={`${dataRef}-marketing`}
              />
            </Form.Item>

            {errorMessage && (
              <Form.Item>
                <AlertBox message={errorMessage} />
              </Form.Item>
            )}

            {successMessage && (
              <Form.Item>
                <Form.SuccessMessage
                  message={successMessage}
                  dataRef={`${dataRef}-success`}
                />
              </Form.Item>
            )}

            <Form.Item>
              <Form.Submit
                expanded
                label={ctaLabel || 'Invia la tua candidatura'}
                dataRef={`${dataRef}-submit`}
                dataTracking={dataTracking}
                color={submitColor}
              />
              <Spacing margin={[SpacingName.SMALLER, 0, 0]}>
                <PrivacyPolicyFooter />
              </Spacing>
            </Form.Item>
          </>
        );
      }}
    </Form>
  );
};

export default JobApplicationForm;
